import { API_CONFIG } from './config';
import {
  getAppSessionCredentials,
  getAppSessionTransport,
  readAppSessionToken,
} from './session';

const FETCH_TRANSPORT_MARKER = '__xprojexAppSessionFetchTransport' as const;

type TransportFetch = typeof fetch & {
  [FETCH_TRANSPORT_MARKER]?: boolean;
};

const resolveRequestUrl = (input: RequestInfo | URL): URL | null => {
  try {
    const rawUrl = input instanceof Request ? input.url : String(input);
    return new URL(rawUrl, window.location.origin);
  } catch {
    return null;
  }
};

const isRelativeApiRequest = (input: RequestInfo | URL, resolvedUrl: URL): boolean => {
  if (input instanceof Request || input instanceof URL) return false;
  const rawUrl = String(input).trim();
  return rawUrl.startsWith('/') && resolvedUrl.pathname.startsWith('/api/');
};

const hasExplicitPublicCredentials = (
  input: RequestInfo | URL,
  init?: RequestInit,
): boolean => (
  init?.credentials === 'omit'
  || (init?.credentials === undefined && input instanceof Request && input.credentials === 'omit')
);

export const createAppSessionFetchTransport = (
  baseFetch: typeof fetch,
  hostname = window.location.hostname,
  backendUrl = API_CONFIG.BACKEND_URL,
): typeof fetch => {
  const backendOrigin = new URL(backendUrl, window.location.origin).origin;

  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const resolvedUrl = resolveRequestUrl(input);
    if (
      !resolvedUrl
      || (resolvedUrl.origin !== backendOrigin && !isRelativeApiRequest(input, resolvedUrl))
      || hasExplicitPublicCredentials(input, init)
    ) {
      return baseFetch(input, init);
    }

    const headers = new Headers(input instanceof Request ? input.headers : undefined);
    new Headers(init?.headers).forEach((value, key) => headers.set(key, value));

    if (getAppSessionTransport(hostname) === 'bearer-tab' && !headers.has('Authorization')) {
      const token = readAppSessionToken();
      if (token) headers.set('Authorization', `Bearer ${token}`);
    }

    return baseFetch(input, {
      ...init,
      headers,
      credentials: getAppSessionCredentials(hostname),
    });
  };
};

export const installAppSessionFetchTransport = (): void => {
  const currentFetch = window.fetch as TransportFetch;
  if (currentFetch[FETCH_TRANSPORT_MARKER]) return;

  const transportFetch = createAppSessionFetchTransport(currentFetch.bind(window)) as TransportFetch;
  Object.defineProperty(transportFetch, FETCH_TRANSPORT_MARKER, { value: true });
  window.fetch = transportFetch;
};
